import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'

import TimeAgo from 'react-timeago'

import Table from 'components/table/SimpleTable'
import Window from 'components/dialog/Window'

import poolSelectors from 'store/selectors/pool'
import routerActions from 'store/modules/router'

import { handlers } from 'store/utils/api'

import {FlexibleXYPlot, XAxis, YAxis, HorizontalGridLines, LineSeries, HeatmapSeries, LabelSeries, Hint} from 'react-vis';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4),
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
  },
  title: {
    flexGrow: 1,
  },
  addButton: {
    flexGrow: 0,
  },
  actionButton: {
    margin: theme.spacing(1),
  }
}))

const FIELDS = [{
  title: 'ID',
  name: 'short_id',
}, {
  title: 'Name',
  name: 'name',
}, {
  title: 'State',
  name: 'state',
}, {
  title: 'Created',
  name: 'created_at',
}, {
  title: 'Prewarm',
  name: 'prewarm',
}, {
  title: 'Max',
  name: 'max',
}, {
  title: 'Running VMs',
  name: 'running_vms',
}, {
  title: 'Error VMs',
  name: 'running_vms',
}, {
  title: 'Off VMs',
  name: 'running_vms',
}, {
  title: 'Ready Leases',
  name: 'ready_leases',
}, {
  title: 'Assigned Leases',
  name: 'assigned_leases',
}, {
  title: 'Waiting Leases',
  name: 'waiting_leases',
}, {
  title: 'Metadata',
  name: 'meta',
}]

const Pools = ({

}) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [ deletingItem, setDeletingItem ] = useState(null)
  const [ value, setValue ] = useState(false)
  const data = useSelector(poolSelectors.data)

  const onCancelDelete = useCallback(() => {
    setDeletingItem(null)
  })

  const onConfirmDelete = useCallback(async () => {
    // const result = await dispatch(itemActions.delete(deletingItem.id))
    // if(result) {
    //   setDeletingItem(null)
    // }
  }, [
    deletingItem,
  ])

  const tableData = useMemo(() => {
    var d = data.map(pool => {
      return {
        short_id: pool.id.substr(0, 7),
        id: pool.id,
        name: pool.name,
        state: pool.state,
        prewarm: pool.config.prewarm_pool_size,
        created_at_date: new Date(pool.created_at),
        created_at: <TimeAgo date={pool.created_at} />,
        max: pool.config.max_pool_size,
        vms: pool.vms.length,
        running_vms: pool.vms.filter((vm) => vm.state == "running").length,
        error_vms: pool.vms.filter((vm) => vm.state == "error").length,
        off_vms: pool.vms.filter((vm) => vm.state == "off").length,
        leases: pool.leases.length,
        assigned_leases: pool.leases.filter((lease) => lease.state == "assigned").length,
        ready_leases: pool.leases.filter((lease) => lease.state == "ready").length,
        waiting_leases: pool.leases.filter((lease) => lease.state == "waiting").length,
        meta: Object.entries(pool.meta).map(([key, value]) => <span key={key}>{key}: {value}</span>),
      }
    })
    d = d.filter(e => e.state != "deleted")
    d.sort((a, b) => {
      if(a.created_at_date > b.created_at_date) return -1
      else return 1
    })
    return d
  }, [
    data,
  ])
  // left to right
  const sortOrder = {
    // lease only
    "waiting": 10,
    // matched vm/lease
    "running": 20, // <- start these at x=0
    "starting": 30,
    // vm only
    "ready": 40,
    "readying": 50,
    // other... error states etc
    "other": 60,
  }
  const colours = {
    "waiting": "pink",
    // matched vm/lease
    "starting": "moccasin",
    "running": "gold",
    // vm only
    "ready": "lightgreen",
    "readying": "lightblue",
    // other... error states etc
    "other": "lightcoral",
  }

  const visData = useMemo(() => {
    var displays = data.filter(e => e.state != "deleted").map(pool => {

      var vms = pool.vms
      vms = vms.filter(v => v.state != "deleted")
      var leases = pool.leases
      leases = leases.filter(l => l.state != "complete")

      var vmMap = vms.reduce(function(obj, x) {
          obj[x.uuid] = x;
          return obj;
      }, {});

      // join on lease.vm == vm.uuid

      // leases that have no vm
      var waitingLeases = []
      // leases that have vms
      var matched = []
      // vms that have no lease
      var unleasedVms = []

      var matchMap = {}
      var display = []

      for (const lease of leases) {
        // could be just if (l.vm) but this way we know that we know about it, too...
        if (vmMap.hasOwnProperty(lease.vm)) {
          // a match!
          matchMap[lease.vm] = true
          const vm = vmMap[lease.vm]
          if (vm.state == "running") {
            display.push({type: "running",
                          vm: vm,
                          lease: lease})
          } else if (vm.state == "starting") {
            display.push({type: "starting",
                          vm: vm,
                          lease: lease})
          } else {
            display.push({type: "other",
                          vm: vm,
                          lease: lease})
          }
        } else if(lease.state != "error") {
          // lease only
          display.push({type: "waiting",
                        vm: null,
                        lease: lease})
          waitingLeases.push(lease)
        }
      }
      for (const vm of vms) {
        if (!matchMap.hasOwnProperty(vm.uuid)) {
          // this vm wasn't matched against any lease
          if (vm.state == "running") {
            display.push({type: "ready",
                          vm: vm,
                          lease: null})
          } else if (vm.state == "starting") {
            display.push({type: "readying",
                          vm: vm,
                          lease: null})
          } else {
            display.push({type: "other",
                          vm: vm,
                          lease: null})
          }
        }
      }

      display.sort((a, b) => {
        if(sortOrder[a.type] > sortOrder[b.type]) return 1
        else return -1
      })

      let i = 0
      for (const d of display) {
        // offset waiting leases to the left
        d.x = i - waitingLeases.length
        i++
      }
      return {pool: pool, display: display}
    })

    let i = 0
    for (const display of displays) {
      display.y = display.pool.id.substr(0, 7) + (display.pool.meta.slot && (" " + display.pool.meta.slot) || "")
      i++
    }
    let result = []

    // now flatten...
    for (const pool of displays) {
      for (const thing of pool.display) {
        let cell = {
          x: thing.x,
          y: pool.y,
          color: colours[thing.type],
          details: thing,
          pool: pool.pool,
        }
        result.push(cell)
      }
    }

    return {
      poolIds: displays
        .filter(x => x.display.length > 0)
        .map(x => x.pool.id.substr(0, 7) + (x.pool.meta.slot && (" " + x.pool.meta.slot) || "")),
      points: result,
    }
  }, [
    data,
  ])

  const getActions = useCallback((item) => {
    return (
        <>
        {item.state == "error" &&
          (<Button
            className={ classes.actionButton }
            size="small"
            variant="outlined"
            onClick={ (e) => {
              // retry building an error pool by flicking it back to unknown,
              // whence the backend should switcharoo to building
              //
              // TODO: talk to Kai about where this sort code should really go
              // and error handling and disabling the button as soon as it's
              // clicked and stuff
              handlers.put(`/pools/${item.id}/state`, {pool_id: item.id, state: "unknown"})
            }}
          >
            Retry
          </Button>)
        }
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          onClick={ (e) => {
            if (window.confirm("Are you sure?")) {
              handlers.put(`/pools/${item.id}/state`, {pool_id: item.id, state: "deleting"})
            }
          }}
        >
          Delete
        </Button>
        <Button
          className={ classes.actionButton }
          size="small"
          variant="outlined"
          onClick={ (e) => {
            console.log(item)
            dispatch(routerActions.navigateTo('pool_logs', {id: item.id}))
          }}
        >
          Pool Logs
        </Button>
        </>
    )
  })

  return (
    <div className={ classes.root }>
      <div className={ classes.toolbar }>
        <div className={ classes.title }>
          <Typography gutterBottom variant="h6">
            Pools
          </Typography>
        </div>
        <div className={ classes.addButton }>
        </div>
      </div>
      <div className={ classes.content }>

				<FlexibleXYPlot
					yType="ordinal"
					yDomain={visData.poolIds}
					margin={50}
					height={visData.poolIds.length*200}
					>
				<XAxis orientation="top"/>
				<YAxis width={200} />
				<HeatmapSeries
					colorType="literal"
					getColor={d => d.color}
					style={{
						stroke: 'gray',
						strokeWidth: '1px',
						rectStyle: {
							rx: 0,
							ry: 0
					}
					}}
					className="heatmap-series-example"
					data={visData.points}
					onValueClick={v => {
            console.log("CLICK", v)
            if (v.details.vm) {
              dispatch(routerActions.navigateTo('vm_logs', {
                pool_id: v.pool.id, vm_uuid: v.details.vm.uuid, lease_id: v.details.lease && v.details.lease.id || "unknown"
              }))
            } else {
              alert("No vm yet")
            }
          }}
					onValueMouseOver={v => {
            setValue({
              type: v.details.type,
              pool: v.pool.meta && v.pool.meta.slot,
              lease: v.details.lease && v.details.lease.meta && v.details.lease.meta.slot,
              vm: v.details.vm && v.details.vm.uuid,
              poolDebug: <pre>{JSON.stringify(v.pool, null, 2).substring(0, 400)}...</pre>,
              leaseDebug: <pre>{JSON.stringify(v.details.lease, null, 2).substring(0, 400)}...</pre>,
              vmDebug: <pre>{JSON.stringify(v.details.vm, null, 2).substring(0, 400)}...</pre>,
              x: v.x,
              y: v.y,
            })
          }}
					onSeriesMouseOut={v => setValue(false)}
					/>
				<LabelSeries
					style={{pointerEvents: 'none', fontSize: "x-small"}}
					rotation={270}
					data={visData.points}
					labelAnchorX="middle"
					labelAnchorY="baseline"
					getLabel={d => `${d.details.lease && d.details.lease.meta && d.details.lease.meta.slot || d.details.type}`}
					/>
				{value !== false && <Hint value={value} />}
				</FlexibleXYPlot>

        <Table
          data={ tableData }
          fields={ FIELDS }
          getActions={ getActions }
        />
      </div>
      {
        deletingItem && (
          <Window
            open
            withCancel
            title="Confirm delete"
            submitTitle="Delete"
            onCancel={ onCancelDelete }
            onSubmit={ onConfirmDelete }
          >
            <Typography>
              Are you absolutely sure you want to delete the { deletingItem.name } pool?
            </Typography>
          </Window>
        )
      }
    </div>
  )
}

export default Pools
